import { useEffect, useRef, useCallback } from 'react';
import useAnalyticsStore from '../stores/analyticsStore';

/**
 * Live analytics event stream ([6.7]). Consumes the [5.7] WS contract:
 *   connect /ws/analytics (cookie-authenticated upgrade)
 *   -> send { type: 'analytics.subscribe', tenantId, cameraId? }
 *   <- { type: 'analytics.events', events: [...] }  -> store.mergeLiveEvents
 *
 * Tenant is the active tenant (localStorage.currentTenantId, same as
 * fetchWithTenant); the server VERIFIES it against the authenticated user, so a
 * spoofed value is rejected, not trusted. cameraId scopes the subscription to a
 * single camera (the embedded camera section); omit it on the global page.
 *
 * Reconnect is gated on a LIVE ref (shouldReconnectRef), not a captured `enabled`
 * closure: onclose fires asynchronously, so reading the ref is what lets an
 * intentional close (toggle off / unmount) avoid a spurious reconnect. Teardown
 * sets the flag false FIRST, then clears the reconnect + ping timers, then
 * close()s the socket, so the async onclose sees "do not reconnect".
 *
 * @param {boolean} enabled
 * @param {{ cameraId?: string }} [opts]
 */
export function useAnalyticsLiveStream(enabled = false, opts = {}) {
  const cameraId = opts.cameraId || null;
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const shouldReconnectRef = useRef(false);
  const mergeLiveEvents = useAnalyticsStore((s) => s.mergeLiveEvents);

  const connect = useCallback(() => {
    // Never open/reopen once disabled or tearing down; never double-open.
    if (!shouldReconnectRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/analytics`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttempts.current = 0;
        // Tenant is proposed here but verified server-side (never trusted).
        const tenantId = localStorage.getItem('currentTenantId') || undefined;
        const sub = { type: 'analytics.subscribe', tenantId };
        if (cameraId) sub.cameraId = cameraId;
        ws.send(JSON.stringify(sub));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'analytics.events' && Array.isArray(data.events)) {
            mergeLiveEvents(data.events);
          }
          // CONNECTED / analytics.subscribed / PONG / analytics.error: no-op here.
        } catch (e) {}
      };

      ws.onclose = () => {
        wsRef.current = null;
        // Reconnect ONLY on an unexpected drop while still enabled. An
        // intentional close (toggle off / unmount) clears the flag first.
        if (!shouldReconnectRef.current) return;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectAttempts.current++;
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {};
    } catch (e) {
      console.error('[AnalyticsLiveWS] connect failed:', e);
    }
  }, [cameraId, mergeLiveEvents]);

  useEffect(() => {
    if (enabled) {
      shouldReconnectRef.current = true;
      reconnectAttempts.current = 0;
      connect();
    }
    return () => {
      // Order matters: kill reconnect intent BEFORE closing, so the async
      // onclose sees the flag and does not re-open.
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      const ws = wsRef.current;
      if (ws) {
        try {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'analytics.unsubscribe' }));
          }
        } catch (e) {}
        ws.close();
        wsRef.current = null;
      }
    };
  }, [enabled, connect]);

  // Keepalive ping (only while enabled).
  useEffect(() => {
    if (!enabled) return undefined;
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'PING' }));
      }
    }, 25000);
    return () => clearInterval(interval);
  }, [enabled]);
}

export default useAnalyticsLiveStream;
